import React, { useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import { customViewerStyles } from "../data/styles/modalStyles";
import NOPIC from "../assets/nopic.svg";
import { useTranslation } from "react-i18next";
import { useMainContext } from "../context/MainContext";
import { colors } from "../data/colors";
import { stdObject } from "../data/types/stdObject";
import BasicButton from "./BasicButton";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadFile } from "../services/downloadFile";

export default function FileViewer({ zIndexs }: stdObject) {
  const { t } = useTranslation();
  const { visor, setVisor, askDeleteFile } = useMainContext();
  const [zoom, setZoom] = useState<number>(1); // Zoom
  const [position, setPosition] = useState({ x: 0, y: 0 }); // Posición de la imagen/PDF
  const containerRef = useRef<HTMLDivElement>(null);
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map()); // Pointers activos
  const pinchDistance = useRef<number | null>(null); // Distancia inicial del pinch
  const pinchZoom = useRef<number>(1); // Zoom inicial del pinch
  const dragStart = useRef({ x: 0, y: 0 }); // Para drag
  const startPosition = useRef({ x: 0, y: 0 });
  const isDragging = useRef(false);

  useEffect(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, [visor.content]);

  // Calcular distancia entre dos dedos
  const getDistance = (
    p1: { x: number; y: number },
    p2: { x: number; y: number },
  ) => {
    const dx = p1.x - p2.x;
    const dy = p1.y - p2.y;

    return Math.sqrt(dx * dx + dy * dy);
  };

  // Pointer Down
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);

    pointers.current.set(e.pointerId, {
      x: e.clientX,
      y: e.clientY,
    });

    // Pinch
    if (pointers.current.size === 2) {
      const [p1, p2] = Array.from(pointers.current.values());

      pinchDistance.current = getDistance(p1, p2);
      pinchZoom.current = zoom;

      isDragging.current = false;

      return;
    }

    // Drag
    if (pointers.current.size === 1) {
      dragStart.current = {
        x: e.clientX,
        y: e.clientY,
      };

      startPosition.current = {
        x: position.x,
        y: position.y,
      };

      isDragging.current = true;
    }
  };

  // Pointer Move
  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(e.pointerId)) {
      return;
    }

    pointers.current.set(e.pointerId, {
      x: e.clientX,
      y: e.clientY,
    });

    // PINCH ZOOM
    if (pointers.current.size === 2) {
      const [p1, p2] = Array.from(pointers.current.values());

      const distance = getDistance(p1, p2);

      if (pinchDistance.current) {
        const scale = distance / pinchDistance.current;
        let newZoom = pinchZoom.current * scale;
        newZoom = Math.min(Math.max(newZoom, 0.5), 4); // Limitar zoom
        setZoom(newZoom);
      }

      return;
    }

    // DRAG
    if (pointers.current.size === 1 && isDragging.current && zoom > 1) {
      const deltaX = e.clientX - dragStart.current.x;

      const deltaY = e.clientY - dragStart.current.y;

      setPosition({
        x: startPosition.current.x + deltaX,
        y: startPosition.current.y + deltaY,
      });
    }
  };

  // Pointer Up
  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(e.pointerId);

    if (pointers.current.size < 2) {
      pinchDistance.current = null;
    }

    if (pointers.current.size === 0) {
      isDragging.current = false;
    }
  };

  // Mouse Wheel Zoom
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();

    const zoomStep = 0.15;

    setZoom((currentZoom) => {
      let newZoom = currentZoom;
      if (e.deltaY < 0) {
        // Scroll arriba
        newZoom += zoomStep;
      } else {
        // Scroll abajo
        newZoom -= zoomStep;
      }
      newZoom = Math.min(Math.max(newZoom, 0.5), 4);
      return newZoom;
    });
  };

  // Zoom +
  const zoomIn = () => {
    setZoom((currentZoom) => Math.min(currentZoom + 0.25, 4));
  };

  // Zoom -
  const zoomOut = () => {
    setZoom((currentZoom) => Math.max(currentZoom - 0.25, 0.5));
  };

  // Reset zoom
  const resetZoom = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const buttons = [
    {
      Icon: DownloadIcon,
      label: t("p.download"),
      onClick: () => downloadFile(visor.name, visor.src),
      color: colors.green,
    },
    {
      Icon: DeleteForeverIcon,
      label: t("p.delete"),
      onClick: askDeleteFile,
      color: colors.red,
    },
    {
      label: t("p.cancel"),
      onClick: () => setVisor({}),
      color: colors.darkGrey,
    },
  ];

  return (
    <Modal
      isOpen={true}
      onRequestClose={() => {}}
      style={customViewerStyles({ ...zIndexs })}
    >
      <div className="documentContainer">
        <div className="documentBar" style={{ backgroundColor: colors.blue }}>
          {visor.name}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 5,
            padding: 10,
            justifyContent: "center",
          }}
        >
          {buttons.map((e: stdObject, index: number) => (
            <BasicButton
              key={index}
              Icon={e.Icon}
              onClick={e.onClick}
              title={e.label}
              color={e.color}
            />
          ))}
        </div>
        <div
          style={{
            flex: 1,
            position: "relative",
            overflow: "hidden",
            background: "#f5f5f5",
          }}
        >
          {/* Controles 
          {visor.ext !== "pdf" && (
            <div
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                zIndex: 100,
                display: "flex",
                gap: 5,
                background: "rgba(255,255,255,0.9)",
                padding: 5,
                borderRadius: 5,
              }}
            >
              <button onClick={zoomOut}>−</button>
              <button onClick={resetZoom}>{Math.round(zoom * 100)}%</button>
              <button onClick={zoomIn}>+</button>
            </div>
          )}*/}

          {/* Viewer */}
          <div
            ref={containerRef}
            onWheel={handleWheel}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              // Importante para pinch
              touchAction: "none",
              cursor: zoom > 1 ? "grab" : "default",
            }}
          >
            <div
              style={{
                transform: `
                    translate(
                        ${position.x}px,
                        ${position.y}px
                    )
                    scale(${zoom})
                `,
                transformOrigin: "center center",
                transition:
                  pointers.current.size === 2
                    ? "none"
                    : "transform 0.05s linear",
                width: visor.ext === "pdf" ? "100%" : "auto",
                height: visor.ext === "pdf" ? "100%" : "auto",
              }}
            >
              {visor.ext === "pdf" ? (
                <iframe
                  src={visor.src}
                  title={visor.name}
                  style={{ width: "100%", height: "100%", border: "none" }}
                />
              ) : (
                <img
                  src={visor.src}
                  onError={(e: any) => (e.target.src = NOPIC)}
                  alt={visor.name}
                  draggable={false}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "500px",
                    objectFit: "contain",
                    userSelect: "none",
                    display: "block",
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
